import { useState, useEffect, useCallback, useMemo } from 'react';
import styled from 'styled-components';
import {
  IoRefreshOutline,
  IoChevronDownOutline,
  IoChevronUpOutline,
  IoSearchOutline,
  IoDocumentTextOutline,
  IoWalletOutline,
} from 'react-icons/io5';
import { useAuth } from '../../../context/AuthContext';
import {
  getPaymentRequests,
  getPaymentHistory,
  getReceivables,
} from '../../../services/settlementService';

const STATUS_BADGE = {
  pending: { label: '결제 대기', bg: '#FFF4E5', color: '#F59E0B' },
  completed: { label: '결제 완료', bg: '#E5EDFF', color: '#4A6CF7' },
  cancelled: { label: '취소', bg: '#F3F4F6', color: '#6B7280' },
  rejected: { label: '거절', bg: '#FEE2E2', color: '#EF4444' },
};

const fmt = (n) => `₩${(n || 0).toLocaleString()}`;

const pad2 = (n) => String(n).padStart(2, '0');
const ymd = (d) => `${d.getFullYear()}-${pad2(d.getMonth() + 1)}-${pad2(d.getDate())}`;
const monthRange = (offset = 0) => {
  const today = new Date();
  const y = today.getFullYear();
  const m = today.getMonth() + offset;
  return { from: ymd(new Date(y, m, 1)), to: ymd(new Date(y, m + 1, 0)) };
};
const parseRowDate = (dateStr) => {
  if (!dateStr) return null;
  const m = /(\d{4})\.\s*(\d{1,2})\.\s*(\d{1,2})/.exec(dateStr);
  if (!m) return null;
  return new Date(Number(m[1]), Number(m[2]) - 1, Number(m[3]));
};

const PaymentManage = () => {
  const { userData } = useAuth();
  const [refreshing, setRefreshing] = useState(false);
  const [expanded, setExpanded] = useState({});
  const [requests, setRequests] = useState([]);
  const [history, setHistory] = useState([]);
  const [receivables, setReceivables] = useState([]);
  const [loading, setLoading] = useState(true);
  const [openPicker, setOpenPicker] = useState(null);

  const [billingRange, setBillingRange] = useState({ mode: 'all', from: '', to: '' });
  const [historyRange, setHistoryRange] = useState({ mode: 'all', from: '', to: '' });
  const [activeTab, setActiveTab] = useState('history');

  const toggleExpand = (id) => setExpanded((prev) => ({ ...prev, [id]: !prev[id] }));

  const loadData = useCallback(async () => {
    const partnerId = userData?.uid;
    if (!partnerId) { setLoading(false); return; }
    const [reqs, hist, recv] = await Promise.all([
      getPaymentRequests(partnerId),
      getPaymentHistory(partnerId),
      getReceivables(partnerId),
    ]);
    setRequests(reqs);
    setHistory(hist);
    setReceivables(recv);
    setLoading(false);
  }, [userData?.uid]);

  useEffect(() => { loadData(); }, [loadData]);

  const handleRefresh = async () => {
    setRefreshing(true);
    await loadData();
    setRefreshing(false);
  };

  const inRangeWith = (row, range) => {
    const d = parseRowDate(row.date);
    if (!d) return true;
    const s = ymd(d);
    return (!range.from || s >= range.from) && (!range.to || s <= range.to);
  };

  const filteredRequests = useMemo(() => requests.filter((r) => inRangeWith(r, billingRange)), [requests, billingRange]);
  const filteredReceivables = useMemo(() => receivables.filter((r) => inRangeWith(r, billingRange)), [receivables, billingRange]);
  const filteredHistory = useMemo(() => history.filter((r) => inRangeWith(r, historyRange)), [history, historyRange]);

  // 청구 내역 — 본사 입력 청구(product_name 있는 것)만 누적 표시
  const billingRows = useMemo(() => filteredRequests
    .filter((r) => r.source === 'admin_request')
    .map((r) => ({
      id: `req-${r.id}`,
      rawId: r.id,
      date: r.date,
      type: '결제요청',
      name: r.productName || r.label || r.method || '-',
      amount: r.amount,
      status: r.status,
      statusLabel: r.statusLabel,
      source: 'req',
      raw: r,
    }))
    .sort((a, b) => (b.date || '').localeCompare(a.date || '')), [filteredRequests]);

  const renderDateFilter = (range, setRange, openKey) => {
    const apply = (mode) => {
      if (mode === 'all') { setRange({ mode, from: '', to: '' }); setOpenPicker(null); }
      else if (mode === 'prev') { const r = monthRange(-1); setRange({ mode, from: r.from, to: r.to }); setOpenPicker(null); }
      else if (mode === 'this') { const r = monthRange(0); setRange({ mode, from: r.from, to: r.to }); setOpenPicker(null); }
    };
    const isOpen = openPicker === openKey;
    return (
      <DateFilterWrap>
        <DateFilterBar>
          <ChipGroup>
            <Chip type="button" $active={range.mode === 'all'} onClick={() => apply('all')}>전체</Chip>
            <Chip type="button" $active={range.mode === 'prev'} onClick={() => apply('prev')}>전월</Chip>
            <Chip type="button" $active={range.mode === 'this'} onClick={() => apply('this')}>당월</Chip>
            <MobileChip type="button" $active={range.mode === 'custom'} onClick={() => setOpenPicker(isOpen ? null : openKey)}>기간지정</MobileChip>
          </ChipGroup>
          <DesktopOnly>
            <VDivider />
            <DateInput type="date" value={range.from} onChange={(e) => setRange({ ...range, mode: 'custom', from: e.target.value })} />
            <Tilde>~</Tilde>
            <DateInput type="date" value={range.to} onChange={(e) => setRange({ ...range, mode: 'custom', to: e.target.value })} />
          </DesktopOnly>
          <SearchIconBtn type="button" onClick={handleRefresh} disabled={refreshing}>
            <IoSearchOutline size={14} />
          </SearchIconBtn>
        </DateFilterBar>
        {isOpen && (
          <DatePickerPanel>
            <PickerRow>
              <PickerLabel>시작일</PickerLabel>
              <DateInput type="date" value={range.from} onChange={(e) => setRange({ ...range, mode: 'custom', from: e.target.value })} />
            </PickerRow>
            <PickerRow>
              <PickerLabel>종료일</PickerLabel>
              <DateInput type="date" value={range.to} onChange={(e) => setRange({ ...range, mode: 'custom', to: e.target.value })} />
            </PickerRow>
            <PickerCloseBtn type="button" onClick={() => setOpenPicker(null)}>적용</PickerCloseBtn>
          </DatePickerPanel>
        )}
      </DateFilterWrap>
    );
  };

  return (
    <Wrap>
      <PageHeader>
        <PageTitle>결제 내역 / 청구 내역 관리</PageTitle>
        <PageDesc>청구내역 및 결제내역을 열람하실 수 있습니다.</PageDesc>
      </PageHeader>

      <TabBar>
        <TabBtn type="button" $active={activeTab === 'history'} onClick={() => setActiveTab('history')}>
          <IoWalletOutline size={16} />
          결제내역
        </TabBtn>
        <TabBtn type="button" $active={activeTab === 'billing'} onClick={() => setActiveTab('billing')}>
          <IoDocumentTextOutline size={16} />
          청구내역
        </TabBtn>
      </TabBar>

      {activeTab === 'billing' && (
        <>
          {renderDateFilter(billingRange, setBillingRange, 'billing')}

          {/* 청구 내역 확인 */}
          <Card>
            <CardHeader>
              <CardTitleRow>
                <IoDocumentTextOutline size={16} />
                <CardTitle>청구 내역 확인</CardTitle>
              </CardTitleRow>
              <RefreshIconBtn type="button" onClick={handleRefresh} disabled={refreshing}>
                <IoRefreshOutline size={14} />
              </RefreshIconBtn>
            </CardHeader>
        {loading ? (
          <EmptyLine>불러오는 중...</EmptyLine>
        ) : billingRows.length === 0 ? (
          <EmptyLine>청구 내역이 없습니다.</EmptyLine>
        ) : (
          <TableWrap>
            <TableHead $cols={BILLING_GRID}>
              <TableCol>일시</TableCol>
              <TableCol>유형</TableCol>
              <TableCol>상품명</TableCol>
              <TableCol $align="right">금액</TableCol>
              <TableCol $align="center">상태</TableCol>
            </TableHead>
            {billingRows.map((row) => (
              <TableRowGroup key={row.id}>
                <TableRow $cols={BILLING_GRID}>
                  <TableCell data-label="일시">{row.date}</TableCell>
                  <TableCell data-label="유형">{row.type}</TableCell>
                  <TableCell data-label="상품명">{row.name}</TableCell>
                  <TableCell data-label="금액" $align="right">{fmt(row.amount)}</TableCell>
                  <TableCell data-label="상태" $align="center">
                    {(() => {
                      const meta = STATUS_BADGE[row.status] || STATUS_BADGE.pending;
                      return <Badge $bg={meta.bg} $color={meta.color}>{meta.label}</Badge>;
                    })()}
                  </TableCell>
                </TableRow>
              </TableRowGroup>
            ))}
          </TableWrap>
        )}
      </Card>
        </>
      )}

      {activeTab === 'history' && (
        <>
          {renderDateFilter(historyRange, setHistoryRange, 'history')}

          {/* 결제 내역 전체 */}
          <Card>
            <CardHeader>
              <CardTitleRow>
                <IoWalletOutline size={16} />
                <CardTitle>결제 내역 전체</CardTitle>
              </CardTitleRow>
              <RefreshIconBtn type="button" onClick={handleRefresh} disabled={refreshing}>
                <IoRefreshOutline size={14} />
              </RefreshIconBtn>
            </CardHeader>
        {loading ? (
          <EmptyLine>불러오는 중...</EmptyLine>
        ) : filteredHistory.length === 0 ? (
          <EmptyLine>결제 내역이 없습니다.</EmptyLine>
        ) : (
          <TableWrap>
            <TableHead $cols={HISTORY_GRID}>
              <TableCol>일시</TableCol>
              <TableCol>유형</TableCol>
              <TableCol>상품명</TableCol>
              <TableCol $align="right">금액</TableCol>
              <TableCol $align="center">상태</TableCol>
              <TableCol $align="center">상세</TableCol>
            </TableHead>
            {filteredHistory.map((h) => {
              const isExpanded = expanded[h.id];
              const isDbView = (h.label || '').includes('열람') || (h.label || '').includes('DB');
              const typeLabel = isDbView ? '결제요청' : '미수금';
              const baseLabel = h.label === '예약완료 미수금' ? '계약완료 수수료' : (h.label || '-');
              const displayLabel = h.isBulk ? `{일괄} ${baseLabel}` : baseLabel;
              const productTypeLabel = h.typeBreakdown || h.categoryLabel || baseLabel;
              return (
                <TableRowGroup key={h.id}>
                  <TableRow $cols={HISTORY_GRID}>
                    <TableCell data-label="일시">{h.date}</TableCell>
                    <TableCell data-label="유형">{typeLabel}</TableCell>
                    <TableCell data-label="상품명">{displayLabel}</TableCell>
                    <TableCell data-label="금액" $align="right">{fmt(h.amount)}</TableCell>
                    <TableCell data-label="상태" $align="center">
                      <Badge $bg={STATUS_BADGE.completed.bg} $color={STATUS_BADGE.completed.color}>결제완료</Badge>
                    </TableCell>
                    <TableCell data-label="상세" $align="center">
                      <ExpandBtn type="button" onClick={() => toggleExpand(h.id)}>
                        {isExpanded ? '접기' : '상세보기'}
                        {isExpanded ? <IoChevronUpOutline size={12} /> : <IoChevronDownOutline size={12} />}
                      </ExpandBtn>
                    </TableCell>
                  </TableRow>
                  {isExpanded && (
                    <DetailPanel>
                      <DetailLine>
                        <DetailKey>상품유형</DetailKey>
                        <DetailVal>{productTypeLabel}</DetailVal>
                      </DetailLine>
                      <DetailLine>
                        <DetailKey>청구가격</DetailKey>
                        <DetailVal>{fmt(h.amount)}</DetailVal>
                      </DetailLine>
                      <DetailDivider />
                      <DetailSectionTitle>청구 / 결제 정보</DetailSectionTitle>
                      {Array.isArray(h.serviceItems) && h.serviceItems.length > 0 ? (
                        <DetailLine $col>
                          <DetailKey>서비스 명</DetailKey>
                          <DetailValGroup>
                            {h.serviceItems.map((it, i) => (
                              <div key={i}>{it.customerName} / {it.summary}</div>
                            ))}
                          </DetailValGroup>
                        </DetailLine>
                      ) : (
                        <DetailLine>
                          <DetailKey>서비스 명</DetailKey>
                          <DetailVal>{displayLabel}</DetailVal>
                        </DetailLine>
                      )}
                    </DetailPanel>
                  )}
                </TableRowGroup>
              );
            })}
          </TableWrap>
        )}
      </Card>
        </>
      )}
    </Wrap>
  );
};

export default PaymentManage;

// ───── 스타일 ─────
const Wrap = styled.div`
  display: flex;
  flex-direction: column;

  @media (min-width: 1024px) {
    max-width: 720px;
    margin: 0 auto;
    width: 100%;
  }
`;

const PageHeader = styled.div`
  display: flex;
  flex-direction: column;
  gap: 4px;
  margin-bottom: ${({ theme }) => theme.spacing.md};
`;

const PageTitle = styled.h1`
  font-size: 22px;
  font-weight: 700;
  color: ${({ theme }) => theme.colors.text};
  margin: 0;
  letter-spacing: -0.3px;
`;

const PageDesc = styled.p`
  font-size: 12px;
  color: ${({ theme }) => theme.colors.textSecondary};
  margin: 0;
  line-height: 1.5;
`;

const TabBar = styled.div`
  display: flex;
  gap: 4px;
  padding: 4px;
  background: #F1F5FF;
  border-radius: 10px;
  margin-bottom: 12px;
`;

const TabBtn = styled.button`
  flex: 1;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  gap: 6px;
  padding: 10px 12px;
  background: ${({ $active }) => ($active ? '#fff' : 'transparent')};
  color: ${({ $active, theme }) => ($active ? theme.colors.primary : theme.colors.textSecondary)};
  border: 0;
  border-radius: 8px;
  font-size: 13px;
  font-weight: 700;
  cursor: pointer;
  box-shadow: ${({ $active }) => ($active ? '0 1px 3px rgba(0,0,0,0.08)' : 'none')};
  transition: all 0.15s ease;

  &:hover {
    color: ${({ theme }) => theme.colors.primary};
  }
`;

const ChipGroup = styled.div` display: flex; gap: 2px; `;

const Chip = styled.button`
  padding: 6px 12px;
  border-radius: 8px;
  border: 0;
  background: ${({ $active }) => ($active ? '#E8EDFB' : 'transparent')};
  color: ${({ $active, theme }) => ($active ? theme.colors.primary : theme.colors.textSecondary)};
  font-size: 13px;
  font-weight: 600;
  cursor: pointer;
  white-space: nowrap;
  flex-shrink: 0;

  @media (max-width: 767px) {
    padding: 5px 8px;
    font-size: 12px;
  }
`;

const DateInput = styled.input`
  min-width: 90px;
  padding: 2px 0;
  border: 0;
  background: transparent;
  font-size: 13px;
  color: ${({ theme }) => theme.colors.text};
  outline: none;
  font-family: inherit;
  flex: 1 1 auto;

  &::-webkit-calendar-picker-indicator {
    padding: 0;
    margin: 0 0 0 2px;
    cursor: pointer;
    opacity: 0.6;
  }
`;

const Tilde = styled.span`
  font-size: 13px;
  color: ${({ theme }) => theme.colors.textLight};
  flex-shrink: 0;
`;

const VDivider = styled.span`
  display: inline-block;
  width: 1px;
  height: 18px;
  background: ${({ theme }) => theme.colors.border};
  margin: 0 6px;
  flex-shrink: 0;

  @media (max-width: 767px) {
    margin: 0 3px;
  }
`;

const Card = styled.div`
  background: #fff;
  border-radius: ${({ theme }) => theme.radius.md};
  padding: ${({ theme }) => theme.spacing.base};
  margin-bottom: ${({ theme }) => theme.spacing.md};
  border: 1.5px solid ${({ theme }) => theme.colors.border};
`;

const CardHeader = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
  padding-bottom: 8px;
  border-bottom: 1px solid ${({ theme }) => theme.colors.borderLight};
  margin-bottom: 8px;
`;

const CardTitleRow = styled.div`
  display: flex;
  align-items: center;
  gap: 6px;
  color: ${({ theme }) => theme.colors.text};
`;

const CardTitle = styled.div`
  color: ${({ theme }) => theme.colors.text};
  font-size: 13px;
  font-weight: 700;
`;

const DateFilterWrap = styled.div`
  display: flex;
  flex-direction: column;
  align-items: flex-end;
  margin-bottom: 10px;
  position: relative;

  @media (max-width: 767px) {
    align-items: stretch;
  }
`;

const DateFilterBar = styled.div`
  display: flex;
  align-items: center;
  gap: 4px;
  padding: 6px 10px;
  background: #fff;
  border: 1px solid ${({ theme }) => theme.colors.border};
  border-radius: 10px;
  width: fit-content;
  max-width: 100%;
  flex-wrap: nowrap;

  @media (max-width: 767px) {
    width: 100%;
    padding: 6px 8px;
    justify-content: space-between;
  }
`;

const DesktopOnly = styled.div`
  display: flex;
  align-items: center;
  gap: 4px;

  @media (max-width: 767px) {
    display: none;
  }
`;

const MobileChip = styled.button`
  padding: 6px 12px;
  border-radius: 8px;
  border: 0;
  background: ${({ $active }) => ($active ? '#E8EDFB' : 'transparent')};
  color: ${({ $active, theme }) => ($active ? theme.colors.primary : theme.colors.textSecondary)};
  font-size: 13px;
  font-weight: 600;
  cursor: pointer;
  white-space: nowrap;
  flex-shrink: 0;

  @media (min-width: 768px) {
    display: none;
  }
`;

const DatePickerPanel = styled.div`
  display: flex;
  flex-direction: column;
  gap: 8px;
  margin-top: 6px;
  padding: 12px;
  background: #fff;
  border: 1px solid ${({ theme }) => theme.colors.border};
  border-radius: 10px;
  width: 100%;
  box-shadow: 0 4px 12px rgba(0,0,0,0.06);

  input[type="date"] {
    flex: 1;
    padding: 8px 10px;
    border: 1px solid ${({ theme }) => theme.colors.border};
    border-radius: 6px;
    font-size: 14px;
  }
`;

const PickerRow = styled.div`
  display: flex;
  align-items: center;
  gap: 10px;
`;

const PickerLabel = styled.span`
  font-size: 12px;
  font-weight: 600;
  color: ${({ theme }) => theme.colors.textSecondary};
  min-width: 50px;
`;

const PickerCloseBtn = styled.button`
  align-self: flex-end;
  margin-top: 4px;
  padding: 7px 16px;
  background: ${({ theme }) => theme.colors.primary};
  color: #fff;
  border: 0;
  border-radius: 6px;
  font-size: 12px;
  font-weight: 700;
  cursor: pointer;
`;

const SearchIconBtn = styled.button`
  display: flex;
  align-items: center;
  justify-content: center;
  background: none;
  border: 0;
  padding: 4px;
  color: ${({ theme }) => theme.colors.textSecondary};
  cursor: pointer;
  &:disabled { opacity: 0.5; }
`;

const RefreshIconBtn = styled.button`
  margin-left: auto;
  display: flex;
  background: none;
  border: 0;
  color: ${({ theme }) => theme.colors.textSecondary};
  cursor: pointer;
  padding: 0;
  &:disabled { opacity: 0.5; }
`;

const Badge = styled.span`
  display: inline-block;
  padding: 5px 14px;
  background: #fff;
  color: ${({ $color }) => $color};
  border: 1px solid ${({ $color }) => $color};
  border-radius: 6px;
  font-size: 12px;
  font-weight: 600;
  white-space: nowrap;
`;

const EmptyLine = styled.div`
  padding: 12px 0;
  text-align: center;
  font-size: 12px;
  color: ${({ theme }) => theme.colors.textLight};
`;

const ExpandBtn = styled.button`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  gap: 4px;
  background: none;
  border: 0;
  padding: 0;
  color: ${({ theme }) => theme.colors.textSecondary};
  font-size: 11px;
  font-weight: 500;
  cursor: pointer;
`;

// ───── 결제 내역 전체 테이블 ─────
const TableWrap = styled.div`
  display: flex;
  flex-direction: column;
`;

const BILLING_GRID = '1.4fr 0.8fr 1.6fr 1fr 1.1fr';
const HISTORY_GRID = '1.4fr 0.8fr 1.6fr 1fr 1fr 0.9fr';

const TableHead = styled.div`
  display: grid;
  grid-template-columns: ${({ $cols }) => $cols || HISTORY_GRID};
  gap: 8px;
  padding: 10px 4px;
  border-bottom: 1px solid ${({ theme }) => theme.colors.border};
  font-size: 11px;
  font-weight: 600;
  color: ${({ theme }) => theme.colors.textLight};

  @media (max-width: 767px) {
    display: none;
  }
`;

const TableCol = styled.div`
  text-align: ${({ $align }) => $align || 'left'};
`;

const TableRowGroup = styled.div`
  border-bottom: 1px solid ${({ theme }) => theme.colors.borderLight};
  &:last-of-type { border-bottom: 0; }
`;

const TableRow = styled.div`
  display: grid;
  grid-template-columns: ${({ $cols }) => $cols || HISTORY_GRID};
  gap: 8px;
  padding: 12px 4px;
  align-items: center;
  font-size: 12px;
  color: ${({ theme }) => theme.colors.text};

  @media (max-width: 767px) {
    display: flex;
    flex-direction: column;
    align-items: stretch;
    gap: 6px;
    padding: 12px;
    background: #FAFBFC;
    border-radius: 8px;
    margin: 8px 0;
  }
`;

const TableCell = styled.div`
  text-align: ${({ $align }) => $align || 'left'};
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
  font-weight: ${({ $align }) => ($align === 'right' ? 700 : 500)};

  @media (max-width: 767px) {
    display: flex;
    justify-content: space-between;
    align-items: center;
    text-align: left;
    white-space: normal;
    font-weight: 500;
    font-size: 13px;

    &::before {
      content: attr(data-label);
      color: ${({ theme }) => theme.colors.textLight};
      font-size: 12px;
      font-weight: 500;
      flex-shrink: 0;
      margin-right: 12px;
    }
  }
`;

const DetailPanel = styled.div`
  display: flex;
  flex-direction: column;
  gap: 10px;
  padding: 16px;
  margin: 0 4px 10px;
  background: #F9FAFB;
  border-radius: 8px;
`;

const DetailLine = styled.div`
  display: flex;
  flex-direction: ${({ $col }) => ($col ? 'column' : 'row')};
  align-items: ${({ $col }) => ($col ? 'stretch' : 'baseline')};
  gap: ${({ $col }) => ($col ? '4px' : '12px')};
  font-size: 12px;
`;

const DetailKey = styled.div`
  flex-shrink: 0;
  width: 80px;
  color: ${({ theme }) => theme.colors.textLight};
  font-weight: 500;
`;

const DetailVal = styled.div`
  color: ${({ theme }) => theme.colors.text};
  font-weight: 600;
`;

const DetailValGroup = styled.div`
  display: flex;
  flex-direction: column;
  gap: 3px;
  padding-left: 0;
  color: ${({ theme }) => theme.colors.text};
  font-weight: 600;
  font-size: 12px;
  line-height: 1.5;
  word-break: keep-all;
  white-space: normal;
`;

const DetailDivider = styled.div`
  height: 1px;
  background: ${({ theme }) => theme.colors.border};
  margin: 2px 0;
`;

const DetailSectionTitle = styled.div`
  font-size: 12px;
  font-weight: 700;
  color: ${({ theme }) => theme.colors.textSecondary};
  margin-top: 2px;
`;
